import { FC } from 'react';
import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import {
  Status as PolicyStatus,
  Severity,
} from '@agilelab/plugin-wb-governance-common';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core';
import {
  WbInfoTooltip,
  Severity as WbSeverity,
} from '@agilelab/plugin-wb-platform';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import ErrorIcon from '@material-ui/icons/Error';
import { TerminatingIcon } from '../../ReleaseDetailPage/ProvisioningCard/LatestProvisioningAccordion/Icons';
import {
  KoFilledIcon,
  KoIcon,
  NotStartedIcon,
  OkFilledIcon,
  OkIcon,
  OkPlusIcon,
  TerminatedIcon,
} from '../Icons';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    detailIcon: {
      height: '24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: theme.spacing(2),
    },
    icon: {
      width: '1.3em',
      height: '1.3em',
    },
    animatedIcon: {
      animation: '$spin 2s linear infinite',
    },
    '@keyframes spin': {
      '0%': {
        transform: 'rotate(360deg)',
      },
      '100%': {
        transform: 'rotate(0deg)',
      },
    },
  }),
);

export const DetailIcon: FC<{
  status: DagStatus;
  okWithWarnings?: boolean;
  filled?: boolean;
  severity?: Severity;
  policyStatus?: PolicyStatus;
}> = ({ status, filled, severity, policyStatus, okWithWarnings }) => {
  const classes = useStyles();
  const theme = useTheme();

  return (
    <div className={classes.detailIcon}>
      {
        {
          [DagStatus.FAILED]: (
            <WbInfoTooltip
              severity={WbSeverity.Error}
              title="Failed. You need to solve the errors reported since they will prevent the deployment"
              icon={
                filled ? (
                  <KoFilledIcon
                    htmlColor={theme.palette.error.main}
                    className={classes.icon}
                  />
                ) : (
                  <KoIcon
                    htmlColor={theme.palette.error.main}
                    className={classes.icon}
                  />
                )
              }
            />
          ),
          [DagStatus.ERROR]: (
            <WbInfoTooltip
              severity={WbSeverity.Error}
              title="Failed. You need to solve the errors reported since they will prevent the deployment"
              icon={
                <KoFilledIcon
                  htmlColor={theme.palette.error.main}
                  className={classes.icon}
                />
              }
            />
          ),
          [DagStatus.PASSED]: (
            <WbInfoTooltip
              severity={WbSeverity.Info}
              title={
                okWithWarnings
                  ? 'Completed with warnings'
                  : 'Completed with success'
              }
              icon={
                /* eslint-disable no-nested-ternary */
                filled ? (
                  <OkFilledIcon
                    htmlColor={theme.palette.success.main}
                    className={classes.icon}
                  />
                ) : okWithWarnings ? (
                  <OkPlusIcon
                    htmlColor={theme.palette.success.main}
                    className={classes.icon}
                  />
                ) : (
                  <OkIcon
                    htmlColor={theme.palette.success.main}
                    className={classes.icon}
                  />
                )
              }
            />
          ),
          [DagStatus.NOT_EXECUTED]: (
            <WbInfoTooltip
              severity={WbSeverity.Error}
              title="Failed. You need to solve the errors reported since they will prevent the deployment"
              icon={
                <KoFilledIcon
                  htmlColor={theme.palette.error.main}
                  className={classes.icon}
                />
              }
            />
          ),
          [DagStatus.NOT_STARTED]: (
            <WbInfoTooltip
              title="At least one error was encountered in this execution due to a previous policy, so this check was not executed at all"
              icon={<NotStartedIcon className={`${classes.icon}`} />}
            />
          ),
          [DagStatus.TERMINATED]: (
            <WbInfoTooltip
              title="The execution has been terminated"
              icon={<TerminatedIcon className={`${classes.icon}`} />}
            />
          ),
          [DagStatus.TERMINATING]: (
            <WbInfoTooltip
              title="The execution is being terminated"
              icon={<TerminatingIcon className={`${classes.icon}`} />}
            />
          ),
          [DagStatus.RUNNING]: (
            <AutorenewIcon
              className={`${classes.icon} ${classes.animatedIcon}`}
            />
          ),
          [DagStatus.WAITING]: (
            <AutorenewIcon
              className={`${classes.icon} ${classes.animatedIcon}`}
            />
          ),
          [DagStatus.INFO]: (
            <WbInfoTooltip
              severity={WbSeverity.Info}
              title={`Failed with severity ${severity}. This policy is just a warning, and will not block the deployment, but remember to check what errors made it fail`}
              icon={
                <ErrorIcon
                  className={`${classes.icon}`}
                  htmlColor={theme.palette.primary.main}
                />
              }
            />
          ),
          [DagStatus.NOT_BLOCKING_ERROR]: (
            <WbInfoTooltip
              severity={WbSeverity.Warning}
              title={
                policyStatus === PolicyStatus.Grace
                  ? 'Failed. This policy is in the grace period, so it is not blocking the deployment. Check and solve the errors, since they will block the deployment when this will become enabled'
                  : 'Failed. This policy is deprecated, so it is not blocking the deployment. Check the errors, but remember that this policy will be removed'
              }
              icon={
                <ErrorIcon
                  className={`${classes.icon}`}
                  htmlColor={theme.palette.warning.main}
                />
              }
            />
          ),
          [DagStatus.OK]: (
            <WbInfoTooltip
              severity={WbSeverity.Info}
              title="Compliant"
              icon={
                <OkFilledIcon
                  htmlColor={theme.palette.success.main}
                  className={classes.icon}
                />
              }
            />
          ),
          [DagStatus.WARNING]: (
            <WbInfoTooltip
              severity={WbSeverity.Warning}
              title={`Failed with severity ${severity}. This policy is just a warning, and will not block the deployment, but remember to check what errors made it fail`}
              icon={
                <ErrorIcon
                  className={`${classes.icon}`}
                  htmlColor={theme.palette.warning.main}
                />
              }
            />
          ),
        }[status]
      }
    </div>
  );
};
